import { z } from "zod";
export type WorkspaceRole = "admin" | "member";
export interface Workspace {
  id: string;
  name: string;
  role: WorkspaceRole;
}
export interface WorkspaceIdentity {
  userId: string;
  tenantId: string;
  role: WorkspaceRole;
  workspaceName: string;
}
export interface IdentityGrant {
  organizationId: string;
  name: string;
  role: WorkspaceRole;
}
export type WorkspaceMember = z.infer<typeof memberSchema>;

export const workspaceRoleSchema = z.enum(["admin", "member"]);
export const memberSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string().nullable(),
  kind: z.enum(["human", "service"]),
  role: workspaceRoleSchema,
  active: z.boolean(),
});
export const profileSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string().nullable(),
  role: workspaceRoleSchema,
  workspaceId: z.uuid(),
  workspaceName: z.string(),
  /** `oidc` while the name still follows the identity provider. */
  nameAuthority: z.enum(["oidc", "workspace"]),
  joinedAt: z.string(),
  updatedAt: z.string(),
});
export type WorkspaceProfile = z.infer<typeof profileSchema>;
export const profileInput = z.object({
  name: z.string().trim().min(1).max(120),
});
export const invitationSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  role: workspaceRoleSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
  acceptedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type WorkspaceInvitation = z.infer<typeof invitationSchema>;
export const inviteInput = z.object({
  email: z.email().trim().toLowerCase(),
  role: workspaceRoleSchema,
});
/**
 * A partial update, as PATCH implies and as the route was already written for:
 * it coalesces each field against what is stored, and refuses a body that
 * changes nothing. Requiring both meant a caller could not change a role
 * without restating whether the person is active — and any caller that got that
 * second value from stale state would silently reactivate somebody.
 */
export const memberUpdateInput = z
  .object({
    role: workspaceRoleSchema.optional(),
    active: z.boolean().optional(),
  })
  .refine(
    (value) => value.role !== undefined || value.active !== undefined,
    "A role or active flag is required.",
  );
export const organizationInput = z.object({
  name: z.string().trim().min(1).max(120),
});
export interface VerifiedIdentity {
  userId: string;
  issuer: string;
  subject: string;
  name: string;
  email: string;
}
